import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Users, Mail, Activity } from "lucide-react";
import DemoResetButton from "../../components/demo/DemoResetButton";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const cardCls =
  "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
    });
  } catch { return iso; }
}

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <div className={`${cardCls} p-5`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-white/50 uppercase tracking-wider">{label}</span>
        <Icon size={18} className="text-[#00FF99]/60" />
      </div>
      <div className="text-3xl font-bold text-[#00FF99]">{value}</div>
      {hint && <div className="text-xs text-white/40 mt-1">{hint}</div>}
    </div>
  );
}

export default function SuperadminDashboard() {
  const [orgs, setOrgs]       = useState([]);
  const [users, setUsers]     = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const headers = authHeaders();
      const [oRes, uRes, iRes] = await Promise.all([
        fetch(`${API_BASE}/api/onboarding/orgs`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/admin/users`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/onboarding/staff/invites`, { headers }).catch(() => null),
      ]);
      const safe = async (r) => {
        if (!r || !r.ok) return [];
        try { const d = await r.json(); return Array.isArray(d) ? d : []; } catch { return []; }
      };
      if (cancelled) return;
      setOrgs(await safe(oRes));
      setUsers(await safe(uRes));
      setInvites(await safe(iRes));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const pendingInvites = invites.filter((i) => !i.is_used).length;
  const recentOrgs = [...orgs]
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Building2}
          label="Total Organisations"
          value={loading ? "…" : orgs.length}
        />
        <StatCard
          icon={Users}
          label="Total Users"
          value={loading ? "…" : users.length}
        />
        <StatCard
          icon={Mail}
          label="Pending Invites"
          value={loading ? "…" : pendingInvites}
          hint={loading ? "" : `${invites.length} total`}
        />
        <StatCard
          icon={Activity}
          label="Active This Month"
          value="—"
          hint="Coming soon"
        />
      </div>

      {/* Recent orgs table */}
      <div className={`${cardCls} overflow-hidden`}>
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-white">Recent Organisations</h2>
            <p className="text-xs text-white/40">Last 5 created</p>
          </div>
          <Link
            to="/superadmin/orgs"
            className="text-xs text-[#00FF99] hover:underline"
          >
            View all →
          </Link>
        </div>

        {loading ? (
          <div className="p-8 text-center text-white/50">Loading…</div>
        ) : recentOrgs.length === 0 ? (
          <div className="p-8 text-center text-white/50">No organisations yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-white/60 text-xs uppercase">
              <tr>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Slug</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {recentOrgs.map((o) => (
                <tr key={o.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                  <td className="px-5 py-3 text-white">{o.name || "—"}</td>
                  <td className="px-5 py-3 text-white/60 font-mono text-xs">{o.slug || "—"}</td>
                  <td className="px-5 py-3">
                    <span className={
                      "inline-block px-2 py-0.5 rounded-full text-xs border " +
                      (o.is_active
                        ? "bg-[#00FF99]/10 text-[#00FF99] border-[#00FF99]/30"
                        : "bg-white/5 text-white/50 border-white/10")
                    }>
                      {o.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-white/60">{formatDate(o.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Demo management — reset button surfaces here so the superadmin can
          re-seed the demo org + clear the browser-side onboarding flags
          without leaving the dashboard. */}
      <div style={{
        marginTop: 8,
        padding: "16px 20px",
        background: "#0a0a0a",
        border: "0.5px solid #1a1a1a",
        borderRadius: 12,
      }}>
        <div style={{
          fontSize: 11, color: "#333",
          textTransform: "uppercase", letterSpacing: "0.8px",
          marginBottom: 12,
        }}>
          Demo Management
        </div>
        <DemoResetButton variant="button" />
      </div>
    </div>
  );
}
